import { resetDetail } from "@/core/redux/slices/specification-template";
import { useDispatch, useSelector } from "@/core/redux/store";
import { getSpecificationTemplate } from "@/core/redux/thunks/specificationTemplateThunk";

import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import CustomToast from "@/shared/hooks/custom-toast";
import { specificationService } from "@/shared/services/api/specification-service";
import { ISpecificationTemplate } from "@/shared/services/interface/responses/ISpecificationTemplate";

import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DialogDetail } from "./specification-template-dialog-detail";
import { getDashboardLink } from "@/shared/utils/link";
import { ROUTES } from "@/core/router/routes";

export default function SpecificationTemplateCard({
  template,
  width = "18.5%",
  usedInbox = false,
  itemCount = 120,
  category = "Category",
}: {
  template: ISpecificationTemplate;
  width?: string;
  usedInbox?: boolean;
  itemCount?: number;
  category?: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { detail } = useSelector((state) => state.specificationTemplate);
  const { success } = CustomToast();
  const { me } = useSelector((state) => state.user);

  const getDetail = async () => {
    setOpen(true);
    await dispatch(getSpecificationTemplate(template.id));
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      dispatch(resetDetail());
    }
  };

  const handleCloneSpecification = async () => {
    if (!me?.company?.id || !detail?.specificationId) return;
    try {
      const response = await specificationService.copySpecification({
        companyId: me.company.id,
        specificationId: detail?.specificationId,
        name: detail.specification.name,
      });

      if (response.success) {
        success({
          title: t("specificationCopied", {
            name: detail.specification.name,
          }),
        });
        setOpen(false);
        navigate(
          getDashboardLink(ROUTES.Dashboard.Specification.Create) +
            `?specificationId=${response.data.id}`
        );
      }
    } catch (error) {
      console.error("Clone Error:", error);
    }
  };

  return (
    <>
      <Card
        className="p-0 rounded-[7px] cursor-pointer group hover:border-[#000000] mb-[1.5%]"
        style={{ width: width }}
        onClick={() => getDetail()}
      >
        <div className="w-full h-[247px] bg-zinc-100 rounded-t-[7px] p-[10px] flex flex-col justify-between">
          <div className="w-full flex justify-between">
            <span
              className={
                usedInbox
                  ? "text-[10px] font-600 text-black"
                  : "text-[4.5px] font-600 text-black"
              }
            >
              {t("version")} <span className="text-black font-500">{1.0}</span>
            </span>
            <span
              className={
                usedInbox
                  ? "text-[10px] font-600 text-black"
                  : "text-[4.5px] font-600 text-black"
              }
            >
              {t("date")}{" "}
              <span className="text-black font-500">
                {new Date(template.createdAt || "").toLocaleDateString()}
              </span>
            </span>
          </div>
          <div className="w-full flex justify-center">
            <span
              className={
                usedInbox
                  ? "text-[12px] font-700 text-muted-foreground"
                  : "text-[7.9px] font-700 text-muted-foreground"
              }
            >
              {template.specification.name}
            </span>
          </div>
          <div className="w-full flex justify-between">
            <div className="w-[33%] flex justify-start items-center">
              <Button
                variant="outline"
                size="icon"
                className={`w-[21px] h-[21px] group-hover:bg-black`}
              >
                <Search
                  style={{ width: "11px", height: "11px" }}
                  className="text-black group-hover:text-white"
                />
              </Button>
            </div>
            <div className="w-[33%] flex justify-center items-end pb-[3px]">
              <span
                className={
                  usedInbox
                    ? "text-[8px] font-500 text-black"
                    : "text-[4.5px] font-500 text-black"
                }
              >
                {t("created-by", { name: "Procright" })}
              </span>
            </div>
            <div className="w-[33%] flex justify-center items-center"></div>
          </div>
        </div>
        <div className="w-full flex p-[12px] spacing-y-[8px]">
          <div className="w-full flex  flex-col">
            <span className="text-[14px] font-600 text-[#09090B] truncate text-ellipsis">
              {template.specification.name}
            </span>
            <span className="text-[14px] font-500 text-[#09090B] truncate text-ellipsis">
              {t("category-and-item-count", {
                category: category,
                count: itemCount,
              })}
            </span>
          </div>
        </div>
      </Card>
      <DialogDetail
        open={open}
        handleOpenChange={handleOpenChange}
        template={template}
        handleCloneSpecification={handleCloneSpecification}
      />
    </>
  );
}
